import {Component, inject, Input} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent, RowClickedEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {AsyncPipe, DatePipe} from "@angular/common";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {AgGridAngular} from "ag-grid-angular";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {ICharityPayout} from "../../../interfaces/charity/ICharityPayout";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {BehaviorSubject, Observable} from "rxjs";
import {IDropdownOption} from "../../../interfaces/IDropDownOptions";
import {charityOption} from "../../../interfaces/charity/ICharityNameForGame";
import {FilterableDropdownComponent} from "../../../components/filterable-dropdown/filterable-dropdown.component";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {PipesModule} from "../../../pipes/pipes.module";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {MatDialog} from "@angular/material/dialog";
import {
  CharityPayoutDetailsComponent,
  ICharityPayoutDetailsDialogData
} from "../../../components/dialogs/charity-payout-details/charity-payout-details.component";
import {UtilService} from "../../../services/util.service";
import {
  TableExportButtonsComponent
} from "../../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportColumn, ITableExportRequest, TableExportService} from "../../../services/table-export.service";

@Component({
  selector: 'app-view-charity-payouts',
  standalone: true,
  templateUrl: './view-charity-payouts.component.html',
  imports: [
    AgGridAngular,
    LoadingCardContentComponent,
    AsyncPipe,
    FilterableDropdownComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    PipesModule,
    TableExportButtonsComponent
  ],
  styleUrls: ['./view-charity-payouts.component.scss']
})
export class ViewCharityPayoutsComponent {

  /** Not a charity id, and cannot collide with one: every real option is a GUID. */
  private static readonly ALL_CHARITIES_OPTION_ID = 'all-charities';

  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private datePipe: DatePipe = inject(DatePipe);
  private utilService: UtilService = inject(UtilService);
  private tableExportService: TableExportService = inject(TableExportService);
  private dialog: MatDialog = inject(MatDialog);

  @Input() set gameData(gameP: IGameQueryResult) {
    // Reset state when game changes
    if (this.activeGame && this.activeGame.Id !== gameP.Id) {
      this.resetViewState();
    }
    this.activeGame = gameP;
    this.fetchCharitiesForGame();
    this.fetchAllPayoutsForGame();
  }

  private resetViewState(): void {
    this.allPayouts = [];
    this.charityPayouts.next([]);
    this.charityBalance = null;
    this.totalPayoutAmount = 0;
    this.selectedCharityId = null;
    this.selectedCharityName = '';
  }

  public charityPayouts: BehaviorSubject<ICharityPayout[]> = new BehaviorSubject<ICharityPayout[]>([]);
  public charityPayouts$: Observable<ICharityPayout[]> = this.charityPayouts.asObservable();
  public payoutsGridApi!: GridApi<ICharityPayout>;
  public activeGame: IGameQueryResult | null = null;
  public charityOptions: IDropdownOption[] = [];
  public totalPayoutAmount: number = 0;
  public charityBalance: ICharityBalance | null = null;
  /**
   * Every payout in the game, newest first, as the server ordered them. Held so narrowing to one charity —
   * and clearing back to all of them — is a filter over what is already on screen rather than another
   * request: re-fetching per charity made the table blink and could re-order rows against the grid's sort.
   */
  private allPayouts: ICharityPayout[] = [];
  public selectedCharityId: string | null = null;
  private selectedCharityName: string = '';

  /**
   * Only the payouts read. It used to be one uiState covering everything, so selecting a charity — which
   * filters rows in memory and needs no read at all — put the grid's loading overlay over rows that were
   * already correct while a balance request it does not render finished. The balance strip appears when its
   * figure arrives; nothing has to wait for it.
   */
  public payoutsLoading = false;

  public payoutColDefs: ColDef[] = [
    {
      // First column, and only shown while looking at more than one charity: with a charity selected the
      // heading above the table already names it, and repeating it down every row is noise.
      headerName: 'Charity',
      field: 'CharityName',
      resizable: true,
      sortable: true,
      valueFormatter: params => params.value ? params.value : '—',
      flex: 1
    },
    {
      // "Recorded", not "Payout Date": this is the entry timestamp, and the column two along — Payment Date — is the
      // date the cheque carries.
      headerName: 'Recorded',
      field: 'CreatedOnDate',
      resizable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y, h:mm a');
        return convertedDate ? convertedDate : '';
      },
      sortable: true,
      flex: 1
    },
    {
      headerName: 'Payout Amount',
      resizable: true,
      wrapHeaderText: true,
      sortable: true,
      field: 'PayoutAmount',
      valueFormatter: params => {
        return this.currencyPipe.transform(params.value);
      },
      flex: 1
    },
    {
      // Newest paid-date first, matching the order the server returns - the date a payout has is the date it was paid,
      // not the evening it was typed in.
      headerName: 'Payment Date',
      field: 'PaymentDate',
      resizable: true,
      sortable: true,
      sort: 'desc',
      // A payout's payment date is a calendar date (cheque / EFT / e-transfer), not an instant.
      valueFormatter: params => this.utilService.formatCalendarDate(params.value),
      // Falls back to the entry timestamp exactly as the server's COALESCE does, so a payout with no payment
      // date sits where the server put it instead of being swept to the end by ag-grid's null handling.
      comparator: (_valueA, _valueB, nodeA, nodeB) =>
        ViewCharityPayoutsComponent.compareByDatePaid(nodeA.data, nodeB.data),
      flex: 1
    },
    {
      headerName: 'Reference #',
      field: 'ReferenceNumber',
      resizable: true,
      sortable: true,
      valueFormatter: params => params.value ? params.value : '—',
      flex: 1
    },
    {
      // Who the cheque was made out to. "The charity" for a direct payout rather than a blank, so the column
      // answers the question on every row instead of only the unusual ones.
      headerName: 'Paid to',
      field: 'PaidToPayeeName',
      resizable: true,
      sortable: true,
      valueFormatter: params => params.value ? params.value : 'The charity',
      flex: 1
    },
    {
      headerName: 'Last Updated',
      field: 'LastUpdatedDate',
      resizable: true,
      valueFormatter: params => {
        const convertedDate = this.datePipe.transform(params.value, 'MMM d, y, h:mm a');
        return convertedDate ? convertedDate : '';
      },
      sortable: true,
      flex: 1
    }
  ];

  /**
   * Ascending by the date a payout is filed under — its payment date, or when it was recorded if it has none —
   * with the timestamp breaking ties. ag-grid inverts this for the descending sort the grid opens on.
   */
  private static compareByDatePaid(leftP: ICharityPayout | undefined, rightP: ICharityPayout | undefined): number {
    const left_date = ViewCharityPayoutsComponent.datePaid(leftP);
    const right_date = ViewCharityPayoutsComponent.datePaid(rightP);

    if (left_date !== right_date) {
      return left_date < right_date ? -1 : 1;
    }

    const left_recorded = String(leftP?.CreatedOnDate ?? '');
    const right_recorded = String(rightP?.CreatedOnDate ?? '');

    if (left_recorded === right_recorded) {
      return 0;
    }

    return left_recorded < right_recorded ? -1 : 1;
  }

  /** The calendar date a payout is filed under. Both fields are ISO, so the first ten characters sort. */
  private static datePaid(payoutP: ICharityPayout | undefined): string {
    return String(payoutP?.PaymentDate ?? payoutP?.CreatedOnDate ?? '').slice(0, 10);
  }

  fetchCharitiesForGame(): void {
    if (!this.activeGame) return;

    // Names only — see the payout picker (#1505): this filter never shows contribution totals.
    this.charityPayoutService.fetchCharityNamesByGameId(this.activeGame.Id).subscribe({
      next: (charities) => {
        // "All charities" is offered as an option, not only as an empty box: getting back to the whole game
        // has to be a thing you can pick, since typing a name is what got you out of it.
        this.charityOptions = [
          {id: ViewCharityPayoutsComponent.ALL_CHARITIES_OPTION_ID, name: 'All charities'},
          ...charities.map(charityP => charityOption(charityP))
        ];
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * The whole game's payout history, newest first, which is what the screen opens on. Before #1411 nothing
   * was fetched until a charity was picked, so "which cheques went out last week" could only be answered one
   * charity at a time — 140 of them on the biggest game.
   */
  fetchAllPayoutsForGame(): void {
    if (!this.activeGame) return;

    this.payoutsLoading = true;

    this.charityPayoutService.fetchCharityPayoutsByGameId(this.activeGame.Id).subscribe({
      next: (payouts) => {
        this.allPayouts = payouts;
        this.applyCharityFilter();
        this.payoutsLoading = false;
      },
      error: (err) => {
        this.payoutsLoading = false;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * A charity id narrows the list; null is the "All charities" entry and restores the whole game. The balance
   * summary only means something for a single charity, so it is fetched on narrowing and dropped on clearing
   * rather than being summed across charities — a total owed across every charity is a different figure, and
   * the Balances tab is where it lives.
   */
  onCharitySelected(charityIdP: string | null): void {
    const charityId = charityIdP === ViewCharityPayoutsComponent.ALL_CHARITIES_OPTION_ID ? null : charityIdP;

    this.selectedCharityId = charityId;
    this.selectedCharityName = charityId
      ? this.charityOptions.find(option => option.id === charityId)?.name || ''
      : '';

    this.applyCharityFilter();

    if (!charityId) {
      this.charityBalance = null;

      return;
    }

    this.charityPayoutService.fetchBalanceByCharityId(charityId, this.activeGame?.Id).subscribe({
      next: (balance) => {
        this.charityBalance = balance;
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /** Whether the Charity column carries information: with one charity selected, every row would repeat it. */
  get showingAllCharities(): boolean {
    return this.selectedCharityId === null;
  }

  private applyCharityFilter(): void {
    const visible = this.selectedCharityId
      ? this.allPayouts.filter(payoutP => payoutP.CharityId === this.selectedCharityId)
      : this.allPayouts;

    this.charityPayouts.next(visible);
    this.calculateTotalPayoutAmount(visible);
    this.payoutsGridApi?.setColumnsVisible(['CharityName'], this.showingAllCharities);
  }

  /**
   * The export describes exactly what is on screen: the same columns, the same rows the filter left, and the
   * same summary figures. An export that quietly covered every charity while the screen showed one would be
   * worse than no export at all.
   *
   * The Charity column travels with the same rule as the grid — present while looking at more than one.
   */
  private buildExportRequest(): ITableExportRequest<ICharityPayout> {
    const visible = this.charityPayouts.getValue();

    const charity_column: ITableExportColumn<ICharityPayout>[] = this.showingAllCharities
      ? [{header: 'Charity', value: payoutP => payoutP.CharityName ?? '—'}]
      : [];

    return {
      title: 'Charity payouts',
      subtitle: [this.activeGame?.Name, this.selectedCharityName || 'All charities'].filter(Boolean).join(' — '),
      summary: [
        {label: 'Total payouts', value: `${visible.length}`},
        // The summary is a label/value pair a person reads, not a column anyone sums, so it keeps its
        // dollar sign in both formats.
        {label: 'Total payout amount', value: this.currencyPipe.transform(this.totalPayoutAmount)}
      ],
      rows: visible,
      columns: [
        ...charity_column,
        // "Recorded", the same as the grid calls it: this is when the payout was entered — it carries a time — while the
        // cheque or EFT is dated by Payment date.
        {
          header: 'Recorded',
          value: payoutP => this.datePipe.transform(payoutP.CreatedOnDate, 'MMM d, y, h:mm a') ?? '',
          csvValue: payoutP => this.tableExportService.csvDateTime(payoutP.CreatedOnDate)
        },
        {
          header: 'Payout Amount',
          value: payoutP => this.currencyPipe.transform(payoutP.PayoutAmount),
          csvValue: payoutP => this.tableExportService.csvMoney(payoutP.PayoutAmount),
          numeric: true
        },
        // A calendar date, not an instant. Printed the way the grid prints it, exported as ISO — the locale
        // form is ambiguous (10/08/2026 is two different days depending on who opens the file).
        {
          header: 'Payment date',
          value: payoutP => this.utilService.formatCalendarDate(payoutP.PaymentDate),
          csvValue: payoutP => this.tableExportService.csvDate(payoutP.PaymentDate)
        },
        {header: 'Reference #', value: payoutP => payoutP.ReferenceNumber || '—'},
        {header: 'Paid to', value: payoutP => payoutP.PaidToPayeeName || 'The charity'},
        {header: 'Status', value: payoutP => payoutP.Voided ? 'Voided' : 'Applied'}
      ],
      fileName: this.tableExportService.fileNameFor([
        this.activeGame?.ShortName || this.activeGame?.Name,
        this.selectedCharityName || 'all-charities',
        'payouts'
      ])
    };
  }

  onExportCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }

  onRowClicked(event: RowClickedEvent<ICharityPayout>): void {
    if (!event.data) {
      return;
    }

    const dialog_data: ICharityPayoutDetailsDialogData = {
      payout: event.data,
      // The row's own charity, not the filter's: on the all-charities list the filter names nobody, and
      // the dialog would otherwise title someone else's payout with the last charity that was selected.
      charityName: event.data.CharityName || this.selectedCharityName,
      gameId: this.activeGame?.Id
    };

    this.dialog.open(CharityPayoutDetailsComponent, {data: dialog_data, width: '600px'})
      .afterClosed()
      .subscribe(() => {
        // The dialog can edit a payout's amount, and this list — with the total under it — is held in memory for the
        // tab's lifetime.
        if (dialog_data.payoutUpdated) {
          this.fetchAllPayoutsForGame();
        }
      });
  }

  calculateTotalPayoutAmount(payouts: ICharityPayout[]): void {
    this.totalPayoutAmount = payouts.reduce((sum, payout) => sum + payout.PayoutAmount, 0);
  }

  onPayoutsGridReady(params: GridReadyEvent<ICharityPayout>) {
    this.payoutsGridApi = params.api;
    // The grid is created after the filter may already have been applied, so the column's visibility is set
    // here too — otherwise selecting a charity before the grid existed leaves the Charity column showing.
    this.payoutsGridApi.setColumnsVisible(['CharityName'], this.showingAllCharities);
    this.payoutsGridApi.sizeColumnsToFit({
      defaultMinWidth: 90,
    });
  }
}
